import { useEffect } from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { Mic, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useVoiceInput } from "@/hooks/use-voice-input";
import { cn } from "@/lib/utils";

interface VoiceInputButtonProps {
  onTranscript: (text: string) => void;
  onInterimTranscript?: (text: string) => void;
  onRecordingChange?: (isRecording: boolean) => void;
  disabled?: boolean;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
  showPreview?: boolean;
  compactRecordingUI?: boolean;  // When true, only show stop button during recording (no waveform/timer)
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function WaveformAnimation({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-0.5 h-4", className)}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="w-0.5 bg-red-500 rounded-full animate-pulse"
          style={{
            height: `${Math.random() * 12 + 4}px`,
            animationDelay: `${i * 0.1}s`,
            animationDuration: '0.5s',
          }}
        />
      ))}
    </div>
  );
}

export function VoiceInputButton({ 
  onTranscript, 
  onInterimTranscript,
  onRecordingChange,
  disabled = false, 
  className,
  size = "icon",
  showPreview = false,
  compactRecordingUI = false
}: VoiceInputButtonProps) {
  const [recordingTime, setRecordingTime] = useState(0);
  
  const { 
    isListening, 
    isSupported, 
    transcript,
    interimTranscript,
    toggleListening,
    stopListening 
  } = useVoiceInput({
    onResult: (text) => {
      onTranscript(text);
    },
    onInterimResult: (text) => {
      if (onInterimTranscript) {
        onInterimTranscript(text);
      }
    },
    onError: (error) => {
      console.error("Voice input error:", error);
    },
  });

  useEffect(() => {
    if (onRecordingChange) {
      onRecordingChange(isListening);
    }
  }, [isListening, onRecordingChange]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isListening) {
      setRecordingTime(0);
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isListening]);

  if (!isSupported) {
    return null;
  }

  const displayText = transcript + (interimTranscript ? ` ${interimTranscript}` : "");

  if (isListening) {
    // Compact mode: just show the stop button
    if (compactRecordingUI) {
      return (
        <div className="relative">
          <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-30" />
          <Button
            type="button"
            variant="default"
            size={size}
            onClick={stopListening}
            disabled={disabled}
            className={cn(
              "relative z-10 bg-red-500 hover:bg-red-600 text-white",
              className
            )}
            data-testid="button-voice-input-stop"
            aria-label="Stop recording"
          >
            <Square className="h-3 w-3 fill-current" />
          </Button>
        </div>
      );
    }
    
    // Full mode: show stop button with waveform and timer
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-30" />
            <div className="absolute inset-[-4px] border-2 border-red-400 rounded-full animate-pulse" />
            <Button
              type="button"
              variant="default"
              size={size}
              onClick={stopListening}
              disabled={disabled}
              className={cn(
                "relative z-10 bg-red-500 hover:bg-red-600 text-white",
                className
              )}
              data-testid="button-voice-input-stop"
              aria-label="Stop recording"
            >
              <Square className="h-3 w-3 fill-current" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-full">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <WaveformAnimation />
            <span className="text-xs font-medium text-red-600 dark:text-red-400 tabular-nums">
              {formatTime(recordingTime)}
            </span>
          </div>
        </div>
        
        {showPreview && displayText && (
          <div className="px-3 py-2 bg-muted/50 rounded-lg border border-border/50 text-sm max-h-20 overflow-y-auto">
            <span className="text-foreground">{transcript}</span>
            {interimTranscript && (
              <span className="text-muted-foreground italic"> {interimTranscript}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={size}
          onClick={toggleListening}
          disabled={disabled}
          className={cn("relative", className)}
          data-testid="button-voice-input"
          aria-label="Start voice input"
        >
          <Mic className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>Voice input</p>
      </TooltipContent>
    </Tooltip>
  );
}

interface VoiceInputWithPreviewProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  className?: string;
}

export function VoiceInputWithPreview({ 
  onTranscript, 
  disabled = false,
  className 
}: VoiceInputWithPreviewProps) {
  const [liveText, setLiveText] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  
  const { 
    isListening, 
    isSupported, 
    transcript,
    interimTranscript,
    toggleListening,
    stopListening,
    clearTranscript
  } = useVoiceInput({
    onResult: (text) => {
      onTranscript(text);
    },
    onError: (error) => {
      console.error("Voice input error:", error);
    },
  });

  useEffect(() => {
    setLiveText(transcript + (interimTranscript ? interimTranscript : ""));
  }, [transcript, interimTranscript]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isListening) {
      setRecordingTime(0);
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isListening]);

  const handleStop = () => {
    stopListening();
    clearTranscript();
  };

  if (!isSupported) {
    return null;
  }

  if (isListening) {
    return (
      <div className={cn("flex flex-col gap-2 w-full", className)}>
        <div className="flex items-center gap-3 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-[-4px] border-2 border-red-400 rounded-full animate-pulse" />
            <Button
              type="button"
              variant="default"
              size="icon"
              onClick={handleStop}
              disabled={disabled}
              className="relative z-10 bg-red-500 hover:bg-red-600 text-white"
              data-testid="button-voice-stop"
              aria-label="Stop recording"
            >
              <Square className="h-3 w-3 fill-current" />
            </Button>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-red-600 dark:text-red-400">
                Recording
              </span>
              <WaveformAnimation />
              <span className="text-xs font-medium text-muted-foreground tabular-nums ml-auto">
                {formatTime(recordingTime)}
              </span>
            </div>
            
            {liveText ? (
              <div className="text-sm text-foreground line-clamp-2">
                <span>{transcript}</span>
                {interimTranscript && (
                  <span className="text-muted-foreground italic">{interimTranscript}</span>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground italic">
                Listening... speak now
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={toggleListening}
          disabled={disabled}
          className={className}
          data-testid="button-voice-input"
          aria-label="Start voice input"
        >
          <Mic className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>Voice input</p>
      </TooltipContent>
    </Tooltip>
  );
}
